import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import * as moment from 'moment';

import { Roster, RosterScheduleSummary } from '../../../../../../models/event-type';
import { EventTypeStatus } from '../../../../../../models/event-type-enums';
import { CapitanLocation } from '../../../../../../models/location';
import { EventTypeService } from '../../../../../../services/event-type/event-type.service';
import { LocationService } from '../../../../../../services/location/location.service';
import {
  addFiltersToGetParams,
  dateStringFromISO8601,
  loadIntFilterFromGetParams,
  loadStringFilterFromGetParams,
} from '../../../../../../utils';


interface RosterPageFilters {
  search?: string;
  location_id?: number;
}


@Component({
  selector: 'app-rosters',
  templateUrl: './rosters.component.html',
  styleUrls: ['./rosters.component.css']
})
export class RostersComponent implements OnInit {
  public allRosters: Roster[] = [];
  public filters: RosterPageFilters = {};
  public locations: CapitanLocation[] = [];

  public loadingRosters = true;
  public serverError: number = null;

  public filtersChangedSubject = new Subject<null>();

  constructor(
    private eventTypeService: EventTypeService,
    private locationService: LocationService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit() {
    // Mirror filter changes into the URL (debounced so we don't churn the
    // history while the user is typing).
    this.filtersChangedSubject.pipe(
      debounceTime(300)
    ).subscribe(() => {
      addFiltersToGetParams(this.filters, this.router, this.activatedRoute);
    });

    const initialQPs = this.activatedRoute.snapshot.queryParamMap;
    loadStringFilterFromGetParams(this.filters, 'search', initialQPs);
    loadIntFilterFromGetParams(this.filters, 'location_id', initialQPs);

    this.loadRosters();

    const cached = this.locationService.getLocations();
    if (cached !== null) {
      this.locations = this.sortLocations(cached);
    } else {
      this.locationService.loadLocations().then(() => {
        this.locations = this.sortLocations(this.locationService.getLocations() || []);
      });
    }
  }

  private sortLocations(locations: CapitanLocation[]): CapitanLocation[] {
    return [...locations].sort((a, b) => a.name.localeCompare(b.name));
  }

  public handleFilterChange() {
    this.filtersChangedSubject.next();
  }

  public loadRosters() {
    this.loadingRosters = true;

    this.eventTypeService
      .getRosters(
        1,
        3000,
        { event_type_status_in: EventTypeStatus.active }
      )
      .then(response => {
        this.allRosters = response.results.sort((a, b) => {
          const byEventType = a.event_type_name.localeCompare(b.event_type_name);
          if (byEventType !== 0) {
            return byEventType;
          }
          return a.name.localeCompare(b.name);
        });
      })
      .catch(reason => {
        this.serverError = reason.status;
      })
      .finally(() => { this.loadingRosters = false; });
  }

  public get filteredRosters(): Roster[] {
    const search = this.filters.search || '';
    const words = search.trim().toLowerCase().split(/\s+/).filter(w => w.length > 0);
    return this.allRosters.filter(roster => {
      if (this.filters.location_id != null && roster.location_id !== this.filters.location_id) {
        return false;
      }
      if (words.length === 0) { return true; }
      const haystack = (
        roster.event_type_name + ' ' +
        roster.name + ' ' +
        roster.schedules.map(s => s.name).join(' ')
      ).toLowerCase();
      return words.every(word => haystack.includes(word));
    });
  }

  public scheduleDisplay(schedule: RosterScheduleSummary): string {
    const name = schedule.name || 'Unnamed Schedule';
    const startIsFuture = moment(schedule.start_date).startOf('day').isAfter(moment().startOf('day'));
    const hasEnd = !!schedule.end_date;

    if (startIsFuture && hasEnd) {
      return `${name} from ${dateStringFromISO8601(schedule.start_date)} to ${dateStringFromISO8601(schedule.end_date)}`;
    }
    if (startIsFuture) {
      return `${name} starting ${dateStringFromISO8601(schedule.start_date)}`;
    }
    if (hasEnd) {
      return `${name} ending ${dateStringFromISO8601(schedule.end_date)}`;
    }
    return name;
  }
}
